import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { ClockIcon, AdjustmentsHorizontalIcon, ShieldCheckIcon, DocumentCheckIcon } from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import { useContent } from '../context/ContentContext';

const BENEFIT_ICONS: ComponentType<SVGProps<SVGSVGElement>>[] = [
  ClockIcon, AdjustmentsHorizontalIcon, ShieldCheckIcon, DocumentCheckIcon,
];

export default function FinancingBenefits() {
  const { content } = useContent();
  const d = content.financingBenefits;

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-brand-orange/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-brand-navy/5 rounded-full blur-3xl" />
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-14">
          <span className="inline-block text-brand-orange font-semibold text-sm uppercase tracking-wider mb-3">
            {d.sectionLabel}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-[42px] lg:leading-[1.15] font-serif font-bold text-brand-navy mb-4">
            {d.heading}
          </h2>
          <p className="text-brand-warm-gray text-base sm:text-lg leading-relaxed">
            {d.description}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
          {d.benefits.map((benefit, index) => {
            const Icon = BENEFIT_ICONS[index] || ClockIcon;
            return (
              <div
                key={benefit.title}
                className="bg-brand-off-white border border-gray-200 rounded-2xl p-6 sm:p-7 hover:border-brand-orange/20 transition-all duration-300"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-brand-navy flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-brand-navy">{benefit.title}</h3>
                    <p className="text-brand-warm-gray text-sm mt-0.5">{benefit.description}</p>
                  </div>
                </div>
                <div className="space-y-2.5 pl-[3.75rem]">
                  {benefit.items.map((item) => (
                    <div key={item} className="flex items-center gap-2.5">
                      <CheckCircleIcon className="w-4 h-4 text-brand-orange flex-shrink-0" />
                      <span className="text-brand-warm-gray text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
